import React, { useState } from 'react';

import { Link } from 'react-router-dom';

import { motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart4,
  BookOpen,
  ExternalLink,
  Info,
  LineChart,
  TrendingUp,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ReferenceArea,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Props as DotProps } from 'recharts/types/shape/Dot';

import {
  investmentGrowthSources,
  stageDetails,
  stageToSourceMap,
} from '../data/investmentGrowthSources';
import ModernButton from './ui/ModernButton';

// Extendemos la interfaz DotProps para incluir la propiedad payload
interface CustomDotProps extends DotProps {
  payload?: {
    mes: string;
    [key: string]: any;
  };
}

interface InvestmentGrowthChartProps {
  className?: string;
}

const InvestmentGrowthChart: React.FC<InvestmentGrowthChartProps> = ({ className = '' }) => {
  // Estados para controlar la visualización de información
  const [activeStage, setActiveStage] = useState<string | null>(null);
  const [showSources, setShowSources] = useState<boolean>(false);
  const [highlightedMonth, setHighlightedMonth] = useState<string | null>(null);
  // Datos mejorados para gráfico de crecimiento acumulativo con valores monetizados
  const growthData = [
    {
      mes: 'Ene',
      inversion: 100,
      retorno: 100,
      valorInicial: 10000,
      valorFinal: 10000,
      roi: 0,
      etapa: 'Inversión inicial',
      ahorro: 0,
      eficiencia: 0,
      nuevosClientes: 0,
    },
    {
      mes: 'Feb',
      inversion: 100,
      retorno: 120,
      valorInicial: 10000,
      valorFinal: 12000,
      roi: 20,
      etapa: 'Adaptación',
      ahorro: 1200,
      eficiencia: 8,
      nuevosClientes: 5,
    },
    {
      mes: 'Mar',
      inversion: 100,
      retorno: 150,
      valorInicial: 10000,
      valorFinal: 15000,
      roi: 50,
      etapa: 'Implementación',
      ahorro: 3500,
      eficiencia: 15,
      nuevosClientes: 12,
    },
    {
      mes: 'Abr',
      inversion: 100,
      retorno: 190,
      valorInicial: 10000,
      valorFinal: 19000,
      roi: 90,
      etapa: 'Optimización',
      ahorro: 6000,
      eficiencia: 25,
      nuevosClientes: 18,
    },
    {
      mes: 'May',
      inversion: 100,
      retorno: 240,
      valorInicial: 10000,
      valorFinal: 24000,
      roi: 140,
      etapa: 'Crecimiento',
      ahorro: 9500,
      eficiencia: 35,
      nuevosClientes: 25,
    },
    {
      mes: 'Jun',
      inversion: 100,
      retorno: 300,
      valorInicial: 10000,
      valorFinal: 30000,
      roi: 200,
      etapa: 'Expansión',
      ahorro: 14000,
      eficiencia: 45,
      nuevosClientes: 32,
    },
    {
      mes: 'Jul',
      inversion: 100,
      retorno: 370,
      valorInicial: 10000,
      valorFinal: 37000,
      roi: 270,
      etapa: 'Consolidación',
      ahorro: 19000,
      eficiencia: 55,
      nuevosClientes: 40,
    },
    {
      mes: 'Ago',
      inversion: 100,
      retorno: 450,
      valorInicial: 10000,
      valorFinal: 45000,
      roi: 350,
      etapa: 'Madurez',
      ahorro: 25000,
      eficiencia: 65,
      nuevosClientes: 50,
    },
  ];

  // Puntos de referencia para anotaciones en el gráfico
  const annotationPoints = [
    { mes: 'Feb', retorno: 120, text: 'Primeros resultados' },
    { mes: 'May', retorno: 240, text: 'Punto de inflexión' },
    { mes: 'Ago', retorno: 450, text: 'ROI 350%' },
  ];

  // Paleta de colores mejorada para un aspecto más profesional
  const COLORS = {
    primary: '#3B82F6', // Azul principal
    secondary: '#10B981', // Verde secundario
    accent: '#F59E0B', // Naranja acento
    highlight: '#ff7300', // Naranja brillante para destacados
    primaryLight: 'rgba(59, 130, 246, 0.6)',
    secondaryLight: 'rgba(16, 185, 129, 0.6)',
    background: 'rgba(255, 255, 255, 0.95)',
  };

  // Función para mostrar información detallada al hacer clic en una etapa
  const handleStageClick = (stageName: string) => {
    setActiveStage(activeStage === stageName ? null : stageName);
  };

  // Función para controlar el estado de hover en meses
  const handleMonthHover = (monthName: string | null) => {
    setHighlightedMonth(monthName);
  };

  return (
    <section className={`py-16 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Crecimiento Acumulativo de la Inversión
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
            Análisis detallado de cómo su inversión en nuestros servicios genera retornos
            exponenciales a lo largo del tiempo, con datos basados en casos reales y estudios de
            mercado.
          </p>
          <p className="text-md text-gray-700 max-w-2xl mx-auto italic border-l-4 border-blue-400 pl-4 py-2 bg-blue-50">
            Nuestras soluciones personalizadas están diseñadas para clientes que valoran la
            excelencia y buscan resultados superiores. El gráfico muestra cómo transformamos su
            inversión en valor tangible, con un crecimiento sostenido que supera las expectativas
            convencionales.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
          >
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xl font-semibold text-gray-800 flex items-center">
                <LineChart className="h-5 w-5 mr-2 text-blue-600" />
                Crecimiento Acumulativo de la Inversión
              </h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowSources(!showSources)}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center hover:bg-blue-200 transition-colors"
                >
                  <BookOpen className="h-3 w-3 mr-1" />
                  {showSources ? 'Ocultar fuentes' : 'Ver fuentes de datos'}
                </button>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center">
                  <BarChart4 className="h-3 w-3 mr-1" /> ROI Final: 350%
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" /> Datos validados 2023
                </span>
              </div>
            </div>

            {/* Panel de fuentes de datos */}
            {showSources && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100"
              >
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-semibold text-blue-800 flex items-center">
                    <Info className="h-4 w-4 mr-1" /> Fuentes de datos utilizadas
                  </h5>
                  <button
                    onClick={() => setShowSources(false)}
                    className="text-blue-500 hover:text-blue-700 text-xs"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {investmentGrowthSources.map((source, index) => (
                    <div
                      key={index}
                      className="bg-white p-3 rounded-md shadow-sm border border-blue-100"
                    >
                      <div className="flex justify-between items-start">
                        <h6 className="text-sm font-medium text-gray-800">{source.name}</h6>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                          {source.year}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 mb-2">{source.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{source.organization}</span>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          Verificar fuente <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <div className="h-96">
              {' '}
              {/* Altura aumentada para mejor visualización */}
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={growthData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  onMouseMove={data => {
                    if (data && data.activeLabel) {
                      handleMonthHover(data.activeLabel);
                    }
                  }}
                  onMouseLeave={() => handleMonthHover(null)}
                >
                  <defs>
                    <linearGradient id="colorInversion" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.2} />
                    </linearGradient>
                    <linearGradient id="colorRetorno" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="mes"
                    tick={{ fill: '#4B5563', fontWeight: 500 }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    padding={{ left: 10, right: 10 }}
                  />
                  <YAxis
                    tick={{ fill: '#4B5563' }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    tickFormatter={value => `${value}%`}
                    domain={[0, 500]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    }}
                    formatter={(value, name, props) => {
                      const item = growthData.find(d => d.mes === props.payload?.mes);
                      if (!item) return [value, name];

                      if (name === 'Inversión Base') {
                        return [`${value}%`, 'Inversión Base'];
                      }
                      if (name === 'Retorno Acumulado') {
                        return [
                          <div className="p-1">
                            <div className="font-medium text-lg">
                              {value}% <span className="text-green-600">(ROI: +{item.roi}%)</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div>
                                <div className="text-xs text-gray-500">Inversión inicial:</div>
                                <div className="text-sm font-medium">
                                  ${item.valorInicial.toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Valor actual:</div>
                                <div className="text-sm font-medium text-green-600">
                                  ${item.valorFinal.toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Ahorro mensual:</div>
                                <div className="text-sm font-medium text-blue-600">
                                  ${item.ahorro.toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Eficiencia:</div>
                                <div className="text-sm font-medium">+{item.eficiencia}%</div>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <div className="text-xs font-medium bg-blue-50 p-1 rounded text-blue-800">
                                Etapa: {item.etapa}
                              </div>
                            </div>
                          </div>,
                          'Retorno Acumulado',
                        ];
                      }
                      return [value, name];
                    }}
                    labelFormatter={label => (
                      <span className="font-medium text-gray-800">Mes: {label}</span>
                    )}
                  />
                  <Legend
                    verticalAlign="top"
                    height={36}
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={value => <span className="text-sm font-medium">{value}</span>}
                  />

                  {/* Áreas de referencia para destacar etapas */}
                  {activeStage && (
                    <ReferenceArea
                      x1={growthData.find(d => d.etapa === activeStage)?.mes}
                      x2={growthData.find(d => d.etapa === activeStage)?.mes}
                      strokeOpacity={0.3}
                      fill="#3B82F6"
                      fillOpacity={0.1}
                    />
                  )}

                  <Area
                    type="monotone"
                    dataKey="inversion"
                    name="Inversión Base"
                    stackId="1"
                    stroke={COLORS.primary}
                    fill="url(#colorInversion)"
                    activeDot={{ r: 6, strokeWidth: 1 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="retorno"
                    name="Retorno Acumulado"
                    stackId="1"
                    stroke={COLORS.secondary}
                    fill="url(#colorRetorno)"
                    activeDot={{
                      r: 8,
                      stroke: COLORS.secondary,
                      strokeWidth: 2,
                      onClick: (data: CustomDotProps) => {
                        const item = growthData.find(d => d.mes === data.payload?.mes);
                        if (item) handleStageClick(item.etapa);
                      },
                    }}
                  />

                  {/* Anotaciones en puntos clave */}
                  {annotationPoints.map((point, index) => {
                    const dataPoint = growthData.find(d => d.mes === point.mes);
                    if (!dataPoint) return null;

                    return (
                      <ReferenceDot
                        key={index}
                        x={point.mes}
                        y={point.retorno}
                        r={6}
                        fill={COLORS.highlight}
                        stroke="white"
                        strokeWidth={1}
                      />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Panel explicativo mejorado */}
            <div className="mt-6 bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <h5 className="text-base font-semibold text-gray-800 mb-3 flex items-center">
                <Info className="h-4 w-4 text-blue-500 mr-2" />
                Interpretación del Gráfico
              </h5>

              {/* Puntos clave del gráfico */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {annotationPoints.map((point, index) => {
                  const dataPoint = growthData.find(d => d.mes === point.mes);
                  if (!dataPoint) return null;

                  return (
                    <div
                      key={index}
                      className={`flex items-start p-3 rounded-lg border ${highlightedMonth === point.mes ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-100'} transition-all duration-200`}
                    >
                      <div className="h-5 w-5 rounded-full bg-orange-500 mt-1 mr-3 flex-shrink-0 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{index + 1}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{point.text}</p>
                        <p className="text-xs text-gray-600">
                          Mes {point.mes}:{' '}
                          <span className="font-medium text-green-600">
                            ${dataPoint.valorFinal.toLocaleString()}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          ROI: <span className="font-medium">+{dataPoint.roi}%</span>
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Descripción general */}
              <p className="text-sm text-gray-700 mb-4 border-l-4 border-blue-400 pl-3 py-2 bg-blue-50">
                Este gráfico muestra cómo una inversión inicial de $10,000 crece a lo largo de 8
                meses, alcanzando un valor final de $45,000 (ROI de 350%). Cada etapa representa un
                momento clave en el ciclo de retorno de inversión de nuestros servicios, con
                beneficios cuantificables en términos de eficiencia, ahorro y crecimiento.
              </p>

              <div className="text-xs text-gray-700 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="mb-2 font-medium text-gray-800">
                  Análisis Exclusivo para Clientes Premium:
                </p>
                <p className="mb-2">
                  Nuestras soluciones de élite están diseñadas para empresas que buscan resultados
                  extraordinarios. A diferencia de servicios estándar que ofrecen mejoras
                  incrementales, nuestro enfoque integral transforma fundamentalmente su operación
                  empresarial, generando un crecimiento exponencial del valor.
                </p>
                <p>
                  La curva de crecimiento ilustra cómo nuestras metodologías exclusivas aceleran el
                  retorno de inversión, superando consistentemente las expectativas del mercado y
                  posicionando a su empresa en una categoría superior frente a la competencia.
                </p>
              </div>

              {/* Panel de información de etapa activa */}
              {activeStage && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-semibold text-blue-800 flex items-center">
                      <Info className="h-4 w-4 mr-1" /> Etapa: {activeStage}
                    </h5>
                    <button
                      onClick={() => setActiveStage(null)}
                      className="text-blue-500 hover:text-blue-700 text-xs"
                    >
                      Cerrar
                    </button>
                  </div>

                  <p className="text-xs text-gray-700 mb-3">{stageDetails[activeStage]}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    <div className="bg-white p-2 rounded border border-gray-200">
                      <span className="font-medium text-gray-700">Fuentes de datos:</span>
                      <ul className="mt-1 pl-4 list-disc">
                        {stageToSourceMap[activeStage]?.map(sourceIndex => (
                          <li key={sourceIndex}>
                            <span className="text-blue-600">
                              {investmentGrowthSources[sourceIndex].name}
                            </span>
                            <span className="text-gray-500 text-xs">
                              {' '}
                              ({investmentGrowthSources[sourceIndex].organization})
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-white p-2 rounded border border-gray-200">
                      <span className="font-medium text-gray-700">Datos clave:</span>
                      <div className="mt-1 grid grid-cols-2 gap-1">
                        {growthData.find(d => d.etapa === activeStage) && (
                          <>
                            <div className="text-gray-600">ROI:</div>
                            <div className="font-medium text-green-600">
                              +{growthData.find(d => d.etapa === activeStage)?.roi}%
                            </div>

                            <div className="text-gray-600">Valor:</div>
                            <div className="font-medium">
                              $
                              {growthData
                                .find(d => d.etapa === activeStage)
                                ?.valorFinal.toLocaleString()}
                            </div>

                            <div className="text-gray-600">Ahorro:</div>
                            <div className="font-medium text-blue-600">
                              $
                              {growthData
                                .find(d => d.etapa === activeStage)
                                ?.ahorro.toLocaleString()}
                            </div>

                            <div className="text-gray-600">Eficiencia:</div>
                            <div className="font-medium">
                              +{growthData.find(d => d.etapa === activeStage)?.eficiencia}%
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>

        {/* CTA estratégico después del gráfico de Crecimiento de Inversión */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl shadow-md border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Garantice el Crecimiento de su Inversión
            </h3>
            <p className="text-gray-600 mb-6">
              Nuestros especialistas pueden diseñar una estrategia personalizada para maximizar el
              crecimiento de su inversión con soluciones tecnológicas de vanguardia.
            </p>
            <Link
              to="/contacto"
              onClick={() => window.scrollTo(0, 0)}
              state={{
                subject: 'Solicitud de Evaluación Personalizada de Inversión',
                message:
                  'Me interesa recibir una evaluación personalizada sobre cómo maximizar el crecimiento de mi inversión con soluciones tecnológicas. Quisiera agendar una consulta con sus especialistas para diseñar una estrategia adaptada a las necesidades de mi empresa.',
              }}
            >
              <ModernButton variant="primary" size="md" className="shadow-md">
                Agendar Evaluación Personalizada
                <ArrowRight className="ml-2 h-5 w-5" />
              </ModernButton>
            </Link>
            <div className="mt-4 text-sm text-gray-500 font-medium">
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                Plazas limitadas disponibles
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestmentGrowthChart;
